import { useNavigate } from 'react-router-dom';

import { Button } from 'antd';

import { programConfig, programLabels } from '@/config';
import { getManufacturerByProgram } from '@/libs/util';
import useGlobalConfig from '@/store/global';

import CopayAssistance from './CopayAssistance';
import AlygloSupport from './alyglo';
import CoagadexSupport from './coagadex';
import GammakedSupport from './gammaked';
import GammaplexSupport from './gammaplex';
import GamunexSupport from './gamunex';
import KoateSupport from './koate';
import RyplazimSupport from './ryplazim';
import { supportInfo } from './support/infos';

const SUPPORT_COMPONENTS = {
  alyglo: <AlygloSupport />,
  coagadex: <CoagadexSupport />,
  gammaked: <GammakedSupport />,
  gammaplex: <GammaplexSupport />,
  gamunex: <GamunexSupport />,
  koate: <KoateSupport />,
  ryplazim: <RyplazimSupport />,
  wilate: <CopayAssistance name="wilate" />,
  panzyga: <CopayAssistance name="panzyga" />,
  fibryga: <CopayAssistance name="fibryga" />,
  nuwiq: <CopayAssistance name="nuwiq" />,
  cutaquig: <CopayAssistance name="cutaquig" />,
  octagam: <CopayAssistance name="octagam" />,
};

const ProviderSupport = () => {
  const { program, themeColor } = useGlobalConfig();
  const navigate = useNavigate();
  const name = program.name;
  const medicalProgram = Object.keys(SUPPORT_COMPONENTS);
  const isOctaPharma = getManufacturerByProgram(program.name)?.indexOf('Octapharma') !== -1;
  const info = supportInfo[name as keyof typeof supportInfo];
  const programInfo = programConfig.find((item) => item.Program === program.name);

  const MedicalContent = () => {
    return SUPPORT_COMPONENTS[name as keyof typeof SUPPORT_COMPONENTS] || null;
  };

  if (!medicalProgram.includes(name)) {
    return null;
  }

  return (
    <div className="mx-auto pb-10 text-base">
      <div className="text-xl md:text-3xl font-bold text-[#000000]">
        {!isOctaPharma && (
          <span style={{ color: themeColor }}>{programLabels[name].name}&nbsp;</span>
        )}
        SUPPORT FOR HEALTHCARE PROVIDERS
      </div>

      <MedicalContent />

      <Button
        type="primary"
        onClick={() => navigate('/medical_portal')}
        style={{
          backgroundColor: themeColor,
          borderColor: themeColor,
          width: '40%',
          height: '48px',
          fontSize: '16px',
        }}
        className="mt-10"
      >
        {programLabels[name].label} Portal For Providers
      </Button>

      {name !== 'gamunex' && !isOctaPharma && (
        <>
          <div className="mt-10 text-2xl font-bold">ADDITIONAL REIMBURSEMENT SUPPORT</div>

          <p className="my-2">
            For any inquiries or assistance with reimbursement, please don't hesitate to contact our
            dedicated Support Team at{' '}
            <a
              className="text-blue-500 hover:text-blue-600 hover:underline"
              href={`tel:${programInfo?.SupportTeamPhone}`}
            >
              {programInfo?.SupportTeamPhone}
            </a>
            . We are fully committed to guiding you through any questions or concerns you may
            encounter.
          </p>

          <p className="my-2">
            Please reach out to our Reimbursement Support Team at{' '}
            <a
              className="text-blue-500 hover:text-blue-600 hover:underline"
              href={`tel:${programInfo?.SupportTeamPhone}`}
            >
              {programInfo?.SupportTeamPhone}
            </a>{' '}
            if your patient:
          </p>

          <ul className="my-2 list-disc ml-6">
            <li>Requires benefit verification</li>
            <li>Requires prior authorization</li>
          </ul>

          <p className="my-2">
            We're here to support you every step of the way and eagerly anticipate the opportunity
            to assist you.
          </p>
        </>
      )}

      {isOctaPharma && (
        <>
          <div className="provider-support mt-10 ">
            <div className="text-2xl font-bold">{info.title}</div>
            <p className="mt-6">{info.description}</p>

            {'supportItems' in info && info.supportItems && (
              <ul className="my-2 list-disc ml-6">
                {info.supportItems.map((item, index) => (
                  <li className="my-2" key={index}>
                    {item.label}
                  </li>
                ))}
              </ul>
            )}

            {'freeTrial' in info && info.freeTrial && (
              <div className="free-trial mt-4">
                <h3 className="font-bold">{info.freeTrial.title}</h3>
                {'description' in info.freeTrial && (
                  <p className="my-2">{info.freeTrial.description}</p>
                )}
                <ul className="list-disc ml-6">
                  {info.freeTrial.details.map((detail, index) => (
                    <li className="my-2" key={index}>
                      {detail.content}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {'footer' in info && info.footer && <p className="mt-6">{info.footer}</p>}
          </div>
        </>
      )}

      <div className="mt-8">
        This program is subject to change or discontinuation by&nbsp;
        <b>{getManufacturerByProgram(name)}</b>
        &nbsp;at any time, for any reason, and with or without prior notice. This portal is
        administered by&nbsp;
        <a
          target="_blank"
          href="https://medmonk.com/"
          className="text-blue-500 hover:text-blue-600 hover:underline"
        >
          Medmonk
        </a>
        &nbsp;for the {programLabels[name].label} Program.
      </div>
    </div>
  );
};

export default ProviderSupport;
